import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faBuildingUser, faPlus } from "@fortawesome/pro-solid-svg-icons"
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  useTheme
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { AppContext } from "components/AppContextProvider"
import IconForButton from "components/ButtonIcon"
import { useFormikContext } from "formik"
import { allRoles } from "generated/roles"
import EmailBoxTypography from "pages/issuereports/pages/workorders/components/EmailBox/EmailBoxTypography"
import { useContext, useEffect, useState } from "react"
import * as Yup from "yup"
import { IWorkshopProps, WorkshopData } from "./Workshop.interfaces"
import { Sidebar } from "components/sidedrawer/Sidebar2";
import { StyledListItem } from "shared/styled/General"
import { useTranslation } from "react-i18next"
import { useWorkshop } from "./useCarWorkshop"

const workshopValidationSchema = (t: any) => Yup.object().shape({
  name: Yup.string().required(t("global.name_is_required", "Name is required")).max(150, t("global.name_can_only_150", "Name can be a maximum of 150 characters long")),
  emailAddress: Yup.string()
    .email(t("global.must_be_a_valid_email", "Must be a valid email"))
    .required(t("global.email_is_required", "Email is required"))
    .max(255)
})

const WorkshopNameTextField: React.FC<{ t: any }> = ({ t }) => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<WorkshopData>();
  const theme = useTheme();

  return (
    <StyledListItem>
      <EmailBoxTypography pt={0} pb={1} text={t("global.workshop", "Workshop")} variant="h6" />
      <TextField
        placeholder={t("settings.companies.enter_name_and_company", "Enter name/company")}
        autoComplete="off"
        fullWidth
        id="name"
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        InputProps={{
          readOnly: values.id > 0,
        }}
        sx={{
          backgroundColor: values.id > 0 ? theme.palette.grey[100] : theme.palette.background.paper,
          "& .Mui-disabled": {
            backgroundColor: "lightgrey",
          },
        }}
        error={touched.name && errors.name ? true : false}
        helperText={touched.name && errors.name ? errors.name : null}
        variant="outlined"
      />
    </StyledListItem>
  );
};

const WorkshopEmailTextField: React.FC<{ t: any }> = ({ t }) => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<WorkshopData>()

  return (
    <StyledListItem>
      <Grid container direction="row" alignItems="center">
        <EmailBoxTypography pt={0} pb={1} text={t("global.email", "Email")} variant="h6" />
        <TextField
          placeholder={t("global.enter_email", "Enter email")}
          autoComplete="off"
          fullWidth
          id="emailAddress"
          value={values.emailAddress}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.emailAddress && errors.emailAddress ? true : false}
          helperText={
            touched.emailAddress && errors.emailAddress ? errors.emailAddress : null
          }
          variant="outlined"
        />
      </Grid>
    </StyledListItem>
  )
};

const WorkshopIsActiveSwitch: React.FC<{ t: any }> = ({ t }) => {
  const { values, setFieldValue } = useFormikContext<WorkshopData>()

  return (
    <StyledListItem>
      <FormControlLabel
        control={<Switch onChange={(event) => setFieldValue("isActive", event.target.checked)} checked={values?.isActive} />}
        label={t("global.active", "Active")}
      />
    </StyledListItem>
  )
}

const initialWorkshopValues = {
  id: 0,
  name: "",
  emailAddress: "",
  isActive: true,
}

const WorkshopSidebar = ({ workshop, companyId, handleSubmit }: IWorkshopProps) => {
  const appContext = useContext(AppContext)
  const { data, status, isSuccess, actions } = useWorkshop()
  const initialFormValues = { ...initialWorkshopValues, companyId, }
  const [initialFormikValues, setInitialFormikValues] = useState<WorkshopData>(initialFormValues)
  const { t } = useTranslation()
  const workshopResponse = {
    data,
    status
  }

  useEffect(() => {
    if (!isSuccess) return
    handleSubmit()
    appContext.toggleSideBar("WorkshopForm", false)
  }, [isSuccess])

  useEffect(() => {
    if (!workshop) return
    setInitialFormikValues(workshop)
  }, [workshop])

  return (
    <>
      <Box order={3} mt={{ xs: 2, sm: 0 }} >
        {hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin) && (
          <Button
            onClick={() => {
              appContext.toggleSideBar(true)
              setInitialFormikValues(initialFormValues)
            }}
            variant="contained"
            color="primary"
            startIcon={<IconForButton icon={faPlus as IconProp} color="primary" />}>
            {t("settings.companies.add_workshop", "Add workshop")}
          </Button>
        )}
      </Box>
      {hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin) && (
        <Sidebar
          initialFormikValues={initialFormikValues}
          setInitialFormikValues={setInitialFormikValues}
          validationSchema={workshopValidationSchema(t)}
          handleSubmit={actions.submit}
          formResult={workshopResponse}
          formData={{
            formId: "WorkshopForm",
            titleNew: t("settings.companies.add_new_workshop", "Add new workshop"),
            subTitleNew: t("settings.companies.create_workshop", "Create workshop"),
            titleEdit: t("global.edit", "Edit") + " " + initialFormikValues?.name,
            subTitleEdit: t("settings.companies.edit_workshop", "Edit workshop"),
            submitEditText: t("global.save", "Save"),
            submitText: t("global.save", "Save"),
            icon: faBuildingUser
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <WorkshopNameTextField t={t} />
            <WorkshopEmailTextField t={t} />
            <WorkshopIsActiveSwitch t={t} />
          </Box>
        </Sidebar>
      )}
    </>
  )
}

export default WorkshopSidebar
